import { Outlet, ScrollRestoration, useLocation } from "react-router-dom";
import { Suspense } from "react";
import { AuthProvider } from "../contexts/AuthContext";
import { ToastProvider } from "../providers/toast-provider";
import { A11yAnnouncer } from "../providers/a11y-announcer";
import Navbar from "../components/Navbar";
import ProtectedRoute from "../components/ProtectedRoute";
import { GLOBAL_ADMIN_ONLY_ROLE_NAMES } from "@/auth/roles";
import { CookieConsent, TelemetryGate } from "@/consent";
import { AdminConsentHandler } from "@/components/AdminConsentHandler";

/**
 * Loading fallback for route transitions.
 * Displayed while lazy routes are being loaded.
 */
function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="text-center">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-primary border-r-transparent"></div>
        <p className="mt-4 text-sm text-foreground-muted-light dark:text-foreground-muted-dark">Loading…</p>
      </div>
    </div>
  );
}

const ADMIN_ONLY_PATHS = ["/apps", "/integrations", "/statuses"] as const;

function getRequiredRoles(pathname: string): readonly string[] | undefined {
  return ADMIN_ONLY_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`)) ? GLOBAL_ADMIN_ONLY_ROLE_NAMES : undefined;
}

/**
 * Root layout component for the application.
 * Provides global context providers, navigation, and scroll restoration.
 *
 * IMPORTANT: Wraps Outlet in Suspense with location.key to work around
 * React Router 7 bug where Suspense fallbacks don't show during navigation.
 * See: https://github.com/remix-run/react-router/issues/12474
 */
export default function RootLayout() {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, "") || "/";
  const isFullWidthRoute = pathname.startsWith("/login") || pathname.startsWith("/logout") || pathname === "/admin-consent";
  const isPublicRoute = isFullWidthRoute || pathname === "/" || pathname === "/ai-privacy-security";
  const requiredRoles = isPublicRoute ? undefined : getRequiredRoles(location.pathname);

  const outlet = (
    <Suspense fallback={<RouteLoadingFallback />} key={location.key}>
      <Outlet />
    </Suspense>
  );

  const page = isFullWidthRoute ? outlet : <div className="max-w-7xl mx-auto px-4 pt-4 pb-6">{outlet}</div>;

  return (
    <AuthProvider>
      <TelemetryGate />
      <ToastProvider />
      <A11yAnnouncer />
      <AdminConsentHandler />
      <div className="min-h-screen bg-surface-background-light dark:bg-surface-background-dark text-foreground-light dark:text-foreground-dark transition-colors duration-200">
        <Navbar />
        {/* 
          WORKAROUND for React Router 7 bug #12474:
          Wrap Outlet in Suspense with key={location.key} to force Suspense
          to show fallback during lazy route transitions.

          Authentication and consent routes fill the remaining viewport.
          Public guidance uses the same page container as signed-in pages.
        */}
        {isPublicRoute ? page : <ProtectedRoute requiredRoles={requiredRoles}>{page}</ProtectedRoute>}
      </div>
      {/* Restore scroll position on navigation (forward/back) */}
      <ScrollRestoration />
      <CookieConsent />
    </AuthProvider>
  );
}
